'use client';

import type React from 'react';
import { useMemo } from 'react';
import {
  DataSourceControllerContext,
  type DataSourceControllerContextType,
} from './DataSourceControllerContext';

/**
 * DataSourceController can't directly import stores because it needs to work with different
 * store implementations (GlobalStateStore vs GlobalAppStateStore) in different contexts.
 *
 * This provider injects all needed functions, actions, and reactive variables through context
 * so the controller stays adaptable. Components use selectors (useContextSelector) for
 * fine-grained reactivity and optimal re-renders.
 *
 * DataSourceRecordStore can be used directly, but other stores must go through injected
 * dependencies to keep the abstraction clean.
 */
export function DataSourceControllerProvider({
  children,
  value,
}: {
  children: React.ReactNode;
  value: DataSourceControllerContextType;
}) {
  const contextValue = useMemo(() => value, [value]);

  return (
    <DataSourceControllerContext.Provider value={contextValue}>
      {children}
    </DataSourceControllerContext.Provider>
  );
}
